import {
  BorderRadius,
  Flex,
  LightModeSemanticColors,
  fontSizes,
} from '@enechain/ecloud-designsystem'

import { komaTimeRange } from '~/models/komaToTime'
import { formatKwh } from '~/models/kwhDisplay'
import type { KomaWeather } from '~/models/weather/actualKoma'
import { formatTemperature } from '~/models/weather/temperature'

/**
 * 需要ピークの両端。MAX = warning (高負荷の注意) / MIN = info (低需要) の
 * semantic は design/css/balance.css の `.peak-badge.max` / `.min` が SoT。
 */
export type DemandPeakKind = 'max' | 'min'

/**
 * ピークバッジ / 値の色。詳細のサマリーバー (design `.summary-peaks`) と
 * 一覧カードのピルバッジ (同 `.region-peaks`) が共有する。
 *
 * design 側も両画面で同一クラス (`.peak-badge` / `.peak-max-value`) を共有し、
 * 一覧では 1 行に畳むための geometry だけを足す構成になっている。それに合わせて
 * ここでは色 (= 意味付け) だけを共有し、器 (詳細 = 3 列グリッド / 一覧 = 横並び
 * flex) は各画面が持つ。器ごと共通部品にすると、詳細の MAX/MIN 列揃えが
 * 各ピルの内容幅で崩れる。
 */
export const DEMAND_PEAK_COLORS: Record<
  DemandPeakKind,
  { badgeBg: string; badgeColor: string; valueColor: string }
> = {
  max: {
    badgeBg: LightModeSemanticColors.Surface.warningLow.value,
    badgeColor: LightModeSemanticColors.Text.warningHigh.value,
    valueColor: LightModeSemanticColors.Text.warningHigh.value,
  },
  min: {
    badgeBg: LightModeSemanticColors.Surface.infoLow.value,
    badgeColor: LightModeSemanticColors.Text.infoHigh.value,
    valueColor: LightModeSemanticColors.Brand.cyan.value,
  },
}

type Props = {
  kind: DemandPeakKind
  /** バッジ文言 ("MAX" / "MIN")。 */
  label: string
  /** ピーク実績値 (kWh, 送電端)。 */
  value: number
  /** 単位ラベル。 */
  unit: string
  /** ピークコマの開始時刻 "HH:MM"。 */
  time: string
  /** ピークコマの実績気温・実況天気 (frontend 合成)。 */
  weather: KomaWeather
}

/**
 * 一覧のエリアカードに置く 1 行ぶんのピルバッジ
 * (design/css/balance.css `.region-peaks .region-peak`、doji-bg.md AC-BDB-002)。
 *
 * [バッジ][値 + 単位][時刻レンジ + 天気アイコン + 気温] を baseline 揃えで
 * 横に並べる。9 エリアのカードを開かずに「どのエリアがいつピークか」を読ませる
 * のが目的なので、詳細のサマリーバー (xl 値) より一段小さい geometry を使う。
 */
export const DemandPeakPill = ({
  kind,
  label,
  value,
  unit,
  time,
  weather,
}: Props): React.ReactElement => {
  const colors = DEMAND_PEAK_COLORS[kind]

  return (
    <Flex alignItems="baseline" gap="4px" minWidth={0}>
      <span
        style={{
          fontSize: fontSizes.xs.value,
          fontWeight: 700,
          padding: '1px 6px',
          borderRadius: BorderRadius.radiiMd.value,
          letterSpacing: '0.3px',
          background: colors.badgeBg,
          color: colors.badgeColor,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: fontSizes.md.value,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          lineHeight: 1.1,
          color: colors.valueColor,
        }}
      >
        {formatKwh(value)}
        <span
          style={{
            fontSize: fontSizes.xs.value,
            fontWeight: 500,
            marginLeft: '3px',
            color: LightModeSemanticColors.Text.midEmphasis.value,
          }}
        >
          {unit}
        </span>
      </span>
      <span
        style={{
          fontSize: fontSizes.xs.value,
          color: LightModeSemanticColors.Text.midEmphasis.value,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
      >
        {komaTimeRange(time)}{' '}
        {/* D-1: 13px は design スケール外 (xs=10/sm=12/md=14) の既存値なので
            fontSize prop 化不可 (天気帯の絵文字と同じ扱い)。margin は design
            .peak-wicon の 0 1px。 */}
        <span style={{ fontSize: '13px', lineHeight: 1, margin: '0 1px' }}>
          {weather.emoji}
        </span>
        {formatTemperature(weather.temperature)}
      </span>
    </Flex>
  )
}
